import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCreditsStore } from '../store/creditsStore';
import { Briefcase, Home, MessageSquare, User, LogOut, Coins, FileText, Users, Sparkles, Award, Bot, Layout, Settings, Gift, PenTool, Menu, X } from 'lucide-react';
import { calculateProfileCompletion } from '../lib/profileCompletionService';
import { NotificationCenter } from './NotificationCenter';
import { useState } from 'react';

export function Navigation() {
  const { profile, signOut } = useAuthStore();
  const { balance } = useCreditsStore();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Calculate profile completion for job seekers
  const profileCompletion = profile && profile.role === 'job_seeker'
    ? calculateProfileCompletion(profile)
    : null;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Safety check - if profile is not loaded yet, show minimal navigation
  if (!profile) {
    return (
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">ResuSwipe</span>
            </Link>
            <div className="text-gray-600">Loading...</div>
          </div>
        </div>
      </nav>
    );
  }

  const jobSeekerLinks = [
    { to: '/ai-assistant', icon: Bot, label: 'AI Assistant' },
    { to: '/discover', icon: Home, label: 'Discover' },
    { to: '/swipe', icon: Briefcase, label: 'Jobs' },
    { to: '/jobseeker/resume-builder', icon: PenTool, label: 'Resume Builder' },
    { to: '/jobseeker/templates', icon: Layout, label: 'Templates' },
    { to: '/jobseeker/resumes', icon: FileText, label: 'My Resumes' },
    { to: '/jobseeker/applications', icon: FileText, label: 'Applications' },
    { to: '/jobseeker/referrals', icon: Gift, label: 'Referrals' },
    { to: '/jobseeker/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/jobseeker/settings', icon: Settings, label: 'Settings' },
  ];

  const employerLinks = [
    { to: '/employer/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/employer/jobs', icon: Briefcase, label: 'Jobs' },
    { to: '/employer/applications', icon: Users, label: 'Applications' },
    { to: '/employer/messages', icon: MessageSquare, label: 'Messages' },
  ];

  const adminLinks = [
    { to: '/admin/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/analytics', icon: Layout, label: 'Analytics' },
  ];

  const links =
    profile?.role === 'admin' ? adminLinks :
    profile?.role === 'employer' ? employerLinks :
    jobSeekerLinks;

  return (
    <>
      {/* Desktop & Mobile Top Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">ResuSwipe</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2 text-gray-600 hover:text-orange-600 transition px-2 py-2 rounded-lg hover:bg-orange-50 text-sm"
                >
                  <link.icon className="w-4 h-4" />
                  <span>{link.label}</span>
                </Link>
              ))}

              {profile?.role === 'job_seeker' && (
                <>
                  {profileCompletion && profileCompletion.percentage < 90 && (
                    <Link
                      to="/jobseeker/profile"
                      className="flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition"
                      title={`Profile ${profileCompletion.percentage}% complete`}
                    >
                      <div className="relative w-8 h-8">
                        <svg className="w-8 h-8 transform -rotate-90">
                          <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="3" fill="none" className="text-gray-200" />
                          <circle
                            cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="3" fill="none"
                            strokeDasharray={`${2 * Math.PI * 14}`}
                            strokeDashoffset={`${2 * Math.PI * 14 * (1 - profileCompletion.percentage / 100)}`}
                            className="text-orange-500 transition-all duration-300"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-orange-600">
                          {profileCompletion.percentage}%
                        </span>
                      </div>
                      <span className="text-sm font-medium text-orange-700">Complete Profile</span>
                    </Link>
                  )}
                  <Link
                    to="/credits"
                    className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg hover:from-amber-200 hover:to-orange-200 transition-all cursor-pointer"
                    title="View and purchase credits"
                  >
                    <Coins className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-900">{balance}</span>
                  </Link>
                </>
              )}

              {profile?.role === 'employer' && (
                <Link
                  to="/employer/priority"
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-sm"
                >
                  <Award className="w-4 h-4" />
                  <span>Upgrade</span>
                </Link>
              )}

              {/* Notification Bell */}
              <NotificationCenter userId={profile.id} />

              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition px-3 py-2 rounded-lg hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign out</span>
              </button>
            </div>

            {/* Mobile Menu Button & Credits */}
            <div className="lg:hidden flex items-center gap-3">
              {/* Notification Bell (Mobile) */}
              <NotificationCenter userId={profile.id} />
              {profile?.role === 'job_seeker' && (
                <Link
                  to="/credits"
                  className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg hover:from-amber-200 hover:to-orange-200 transition-all"
                  title="View and purchase credits"
                >
                  <Coins className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-900">{balance}</span>
                </Link>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-4 space-y-2 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {/* Sign Out Button - TOP OF MENU */}
              <button
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 border-2 border-red-200 text-red-600 hover:bg-red-100 rounded-lg transition font-semibold"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>

              {profile?.role === 'job_seeker' && profileCompletion && profileCompletion.percentage < 90 && (
                <Link
                  to="/jobseeker/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition"
                >
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-orange-600" />
                    <span className="font-medium text-orange-900">Complete Profile</span>
                  </div>
                  <span className="text-sm font-bold text-orange-600">{profileCompletion.percentage}%</span>
                </Link>
              )}

              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition"
                >
                  <link.icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              ))}

              {profile?.role === 'job_seeker' && (
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition"
                >
                  <Sparkles className="w-5 h-5" />
                  <span>Upgrade to Premium</span>
                </Link>
              )}

              {profile?.role === 'employer' && (
                <Link
                  to="/employer/priority"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition"
                >
                  <Award className="w-5 h-5" />
                  <span>Upgrade to Premium</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Bottom Navigation (Quick Access) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="flex items-center justify-around px-2 py-2">
          {profile?.role === 'job_seeker' ? (
            <>
              <Link to="/discover" className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-orange-600 transition">
                <Home className="w-6 h-6" />
                <span className="text-xs font-medium">Home</span>
              </Link>
              <Link to="/swipe" className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-orange-600 transition">
                <Briefcase className="w-6 h-6" />
                <span className="text-xs font-medium">Jobs</span>
              </Link>
              <Link to="/ai-assistant" className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-orange-600 transition">
                <Bot className="w-6 h-6" />
                <span className="text-xs font-medium">AI</span>
              </Link>
              <Link to="/jobseeker/messages" className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-orange-600 transition">
                <MessageSquare className="w-6 h-6" />
                <span className="text-xs font-medium">Messages</span>
              </Link>
              <Link to="/jobseeker/settings" className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-orange-600 transition">
                <Settings className="w-6 h-6" />
                <span className="text-xs font-medium">Settings</span>
              </Link>
            </>
          ) : profile?.role === 'employer' ? (
            <>
              <Link to="/employer/dashboard" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <Home className="w-6 h-6" />
                <span className="text-xs font-medium">Home</span>
              </Link>
              <Link to="/employer/jobs" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <Briefcase className="w-6 h-6" />
                <span className="text-xs font-medium">Jobs</span>
              </Link>
              <Link to="/employer/applications" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <Users className="w-6 h-6" />
                <span className="text-xs font-medium">Apps</span>
              </Link>
              <Link to="/employer/messages" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <MessageSquare className="w-6 h-6" />
                <span className="text-xs font-medium">Messages</span>
              </Link>
            </>
          ) : (
            <>
              <Link to="/admin/dashboard" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <Home className="w-6 h-6" />
                <span className="text-xs font-medium">Home</span>
              </Link>
              <Link to="/admin/users" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <Users className="w-6 h-6" />
                <span className="text-xs font-medium">Users</span>
              </Link>
              <Link to="/admin/analytics" className="flex flex-col items-center gap-1 px-4 py-2 text-gray-600 hover:text-orange-600 transition">
                <Layout className="w-6 h-6" />
                <span className="text-xs font-medium">Analytics</span>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Spacer for bottom nav on mobile */}
      <div className="lg:hidden h-16" />
    </>
  );
}
